/**
 * ValidationStatusBadge - Badge per lo stato di validazione documento
 *
 * Mostra lo stato corrente della validazione con icona e colore appropriati:
 * - not_validated: grigio con icona minus
 * - auto_validated: blu con check
 * - human_validated: verde con check-circle
 * - has_errors: rosso con alert-circle
 * - pending_review: warning con alert-triangle
 */

import { BadgeWithIcon } from "@/components/base/badges/badges";
import {
  Minus,
  Check,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
} from "@untitledui/icons";
import { ValidationStatus } from "@/types/api/documents";
import { useTranslation } from "react-i18next";
import { cx } from "@/utils/cx";

interface ValidationStatusBadgeProps {
  status: ValidationStatus | string | null | undefined;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

// Status configuration with colors and icons
const statusConfig: Record<
  string,
  {
    color: "gray" | "blue" | "success" | "error" | "warning";
    icon: typeof Minus;
    labelKey: string;
  }
> = {
  not_validated: {
    color: "gray",
    icon: Minus,
    labelKey: "validation.status.not_validated",
  },
  auto_validated: {
    color: "blue",
    icon: Check,
    labelKey: "validation.status.auto_validated",
  },
  human_validated: {
    color: "success",
    icon: CheckCircle,
    labelKey: "validation.status.human_validated",
  },
  has_errors: {
    color: "error",
    icon: AlertCircle,
    labelKey: "validation.status.has_errors",
  },
  pending_review: {
    color: "warning",
    icon: AlertTriangle,
    labelKey: "validation.status.pending_review",
  },
};

// Fallback translations if i18n keys are not set
const fallbackLabels: Record<string, string> = {
  not_validated: "Non validato",
  auto_validated: "Auto-validato",
  human_validated: "Validato",
  has_errors: "Errori",
  pending_review: "Da revisione",
};

export function ValidationStatusBadge({
  status,
  size = "sm",
  showLabel = true,
  className,
}: ValidationStatusBadgeProps) {
  const { t } = useTranslation("pages:documents");

  // Normalize status to lowercase string
  const normalizedStatus = status?.toLowerCase() ?? "not_validated";

  // Get config or use default
  const config = statusConfig[normalizedStatus] ?? statusConfig.not_validated;

  // Get label with fallback
  const label = t(config.labelKey, fallbackLabels[normalizedStatus] ?? normalizedStatus);

  const IconComponent = config.icon;

  return (
    <BadgeWithIcon
      type="pill-color"
      size={size}
      color={config.color}
      iconLeading={(props) => (
        <IconComponent
          {...props}
          className={cx(props.className)}
        />
      )}
      className={className}
    >
      {showLabel && label}
    </BadgeWithIcon>
  );
}

/**
 * Compact version - only shows icon, no label
 * Useful for table cells or tight spaces
 */
export function ValidationStatusIcon({
  status,
  className,
}: {
  status: ValidationStatus | string | null | undefined;
  className?: string;
}) {
  return (
    <ValidationStatusBadge
      status={status}
      size="sm"
      showLabel={false}
      className={className}
    />
  );
}
